import React from "react";
import { compact } from "lodash";
import { observable, action, computed } from "mobx";
import * as Api from "api";
import * as AgencyApp from "api/admin/AgencyApp";
import * as AppGroup from "api/admin/AppGroup";

interface App {
  id?: number;
  redDmpAppId?: number;
}

interface Value {
  trackingVendorId?: number;
  appGroupId?: number;
  ios: App;
  android: App;
  redUserId?: number;
  isTver?: boolean;
  twitterUserId?: string;
}

export class FormStore {
  public errors = new Api.Error<AgencyApp.CreateParams>();

  @observable.ref
  public appGroups: AppGroup.IndexResponse[] = [];

  @observable.ref
  public open = false;

  @observable.ref
  public edit = false;

  @observable
  public value: Value = {
    ios: {},
    android: {},
  };

  @computed
  public get appGroup() {
    return this.appGroups.find(
      (appGroup) => appGroup.id === this.value.appGroupId
    );
  }

  @action.bound
  public modal(open: boolean, value?: Value) {
    this.open = open;
    this.errors.clear();
    this.value = value || {
      ios: {},
      android: {},
      isTver: false,
    };
    if (open) this.fetchAppGroups();
  }

  @action.bound
  public async fetchAppGroups() {
    const response = await AppGroup.Api.index();
    if (response?.status === Api.Status.OK) {
      this.appGroups = response.data;
    }
  }

  @action.bound
  public async save(agencyId: number) {
    this.errors.clear();

    const apps = compact([
      this.value.ios.id && this.value.ios,
      this.value.android.id && this.value.android,
    ]);

    const response = await AgencyApp.Api.create({
      agencyId,
      apps,
      appGroupId: this.value.appGroupId,
      redUserId: this.value.redUserId,
      isTver: this.value.isTver,
      twitterUserId: this.value.twitterUserId,
    });

    if (response?.status === Api.Status.OK) {
      this.modal(false);
      return true;
    }

    this.errors.set(response?.data);

    return false;
  }
}

const Context = React.createContext<FormStore | null>(null);

export const FormStoreProvider: React.FC<{ value?: FormStore }> = ({
  children,
  value,
}) => {
  const store = React.useMemo(() => (value ? value : new FormStore()), [value]);
  return <Context.Provider value={store}>{children}</Context.Provider>;
};

export const useFormStore = () => {
  const formStore = React.useContext(Context);
  if (!formStore) {
    throw new Error("useFormStore must be used within a FormStoreProvider.");
  }
  return formStore;
};
